'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { CommitmentStatusEnum } from '@sola/schema/dotcom/supporters/commitments.ts';
import { GraphQLCommitment, GraphQLCommitmentType } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { commitmentsQuery, givingTotalsQuery } from '../queries.ts';
import { filterSponsorshipCommitments, sortCommitmentsByPriority, sortSponsorshipCommitmentsByParticipantName } from '../utils/commitments.ts';
import { useSupporterQuery } from './base.ts';
export const useCommitmentSortByPriority = () => {
  const $ = _c(4);
  const {
    data: t0
  } = useSupporterQuery(commitmentsQuery);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? [] : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const commitments = t1;
  let t2;
  if ($[2] !== commitments) {
    t2 = sortCommitmentsByPriority(commitments);
    $[2] = commitments;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const groupedCommitments = t2;
  return groupedCommitments;
};
export const useSponsorships = () => {
  const $ = _c(7);
  const queryResult = useSupporterQuery(commitmentsQuery);
  let t0;
  if ($[0] !== queryResult.data) {
    t0 = queryResult.data ?? [];
    $[0] = queryResult.data;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const commitments = t0;
  let t1;
  if ($[2] !== commitments) {
    t1 = sortSponsorshipCommitmentsByParticipantName(filterSponsorshipCommitments(commitments));
    $[2] = commitments;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const sponsorships = t1;
  let t2;
  if ($[4] !== queryResult || $[5] !== sponsorships) {
    t2 = {
      ...queryResult,
      sponsorships
    };
    $[4] = queryResult;
    $[5] = sponsorships;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};

/**
 * @returns {
 *  amountDue,
 *  hasMissedPayment,
 *  hasDueThisMonth,
 *  alertCommitments,
 *  missedPaymentCommitments,
 *  amountDueCommitments,
 *  payAheadCommitments
 * }
 */
export const useMakeAPaymentCommitments = (): {
  amountDue: number;
  hasMissedPayment: boolean;
  hasDueThisMonth: boolean;
  canPayAhead: boolean;
  alertCommitments: GraphQLCommitment[];
  missedPaymentCommitments: GraphQLCommitment[];
  amountDueCommitments: GraphQLCommitment[];
  payAheadCommitments: GraphQLCommitment[];
} => {
  const {
    data: [commitments = [], givingSummary]
  } = useSupporterQuery([commitmentsQuery, givingTotalsQuery]);

  // TODO: think through commitments coming back faster than giving history.
  // How to handle showing the alert. Does the alert just pop up on screen.
  const {
    totalDueThisMonthAmount,
    totalMissedPaymentAmount
  } = givingSummary ?? {};
  const {
    missedPaymentCommitments,
    amountDueCommitments,
    payAheadCommitments
  } = useMemo(() => {
    return sortCommitmentsByPriority(commitments ?? []).reduce((acc, c) => {
      if (c.status !== CommitmentStatusEnum.Active) {
        return acc;
      }
      if (c.hasMissedPayment) {
        acc.missedPaymentCommitments.push(c);
      } else if (c.hasDueThisMonth) {
        acc.amountDueCommitments.push(c);
      } else if (c.commitmentType !== GraphQLCommitmentType.GiftCommitment) {
        // We don't allow pay-ahead for gift commitments, but do for other commitments that may or may not be on autopay
        acc.payAheadCommitments.push(c);
      }
      return acc;
    }, {
      missedPaymentCommitments: [] as GraphQLCommitment[],
      amountDueCommitments: [] as GraphQLCommitment[],
      payAheadCommitments: [] as GraphQLCommitment[]
    });
  }, [totalDueThisMonthAmount, totalMissedPaymentAmount]);
  return {
    amountDue: (totalDueThisMonthAmount ?? 0) + (totalMissedPaymentAmount ?? 0),
    alertCommitments: missedPaymentCommitments.length > 0 ? missedPaymentCommitments : amountDueCommitments,
    hasMissedPayment: missedPaymentCommitments.length > 0,
    hasDueThisMonth: amountDueCommitments.length > 0,
    canPayAhead: payAheadCommitments.length > 0,
    payAheadCommitments,
    amountDueCommitments,
    missedPaymentCommitments
  };
};